import { ElementType } from "@easy-email-pro-core/constants";
import { Element as NodeElement, TextNode } from "@easy-email-pro-core/typings";
import { BlockManager } from "@easy-email-pro-core/utils/BlockManager";
import { camelCase } from "lodash";

const blockLevelTags = [
  "div",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ol",
  "ul",
  "li",
  "table",
  "hr",
  "dl",
];

const formateChildNotes = (node: Element): ChildNode[] => {
  const childNodes = Array.from(node.childNodes);
  const hasBlockElement = childNodes.some((item) => {
    return item.nodeType === Node.ELEMENT_NODE && isBlockNode({ item });
  });
  if (!hasBlockElement) {
    return childNodes;
  }

  function groupNodesByDisplay(node: Element) {
    const result = [];
    let currentGroup: { display: string; children: ChildNode[] } | null = null;

    for (const child of node.childNodes) {
      let display = "inline";

      if (
        child.nodeType === Node.ELEMENT_NODE &&
        isBlockNode({ item: child })
      ) {
        display = "block";
      }

      if (!currentGroup || currentGroup.display !== display) {
        currentGroup = { display: display, children: [] };
        result.push(currentGroup);
      }

      currentGroup!.children.push(child);
    }

    return result;
  }
  const result = groupNodesByDisplay(node);
  const list: ChildNode[] = [];
  result.forEach((item) => {
    if (item.display === "inline") {
      const div = document.createElement("div");
      item.children.forEach((child) => {
        if (child.textContent?.trim()) {
          div.appendChild(child);
        }
      });
      if (div.childNodes.length > 0) {
        list.push(div);
      }
    } else {
      list.push(...item.children);
    }
  });
  return list;
};

const isBlockNode = ({ item }: { item: Element | ChildNode }) => {
  if (!(item instanceof Element)) return false;
  if (item.tagName.toLocaleLowerCase().includes("mj-")) {
    return true;
  }
  return blockLevelTags.includes(item.tagName.toLowerCase());
};

export const HtmlNodeAdapter = (
  content: string,
  mjmlHandler: (node: ChildNode) => NodeElement | TextNode
) => {
  const div = document.createElement("div");
  div.innerHTML = content;

  return [...div.childNodes].map((node) => {
    return getDOMNode(node, mjmlHandler);
  });
};

export const getDOMNode = (
  node: ChildNode,
  mjmlHandler: (node: ChildNode) => NodeElement | TextNode
): TextNode | NodeElement => {
  if (node.nodeType === Node.TEXT_NODE) {
    return {
      text: node.textContent?.replace(/\s+/g, " ").replace(/\n/g, "") || "",
    };
  } else if (node.nodeType === Node.ELEMENT_NODE && node instanceof Element) {
    const tagName = node.tagName.toLowerCase();
    const attrs: Record<string, string> = {};
    if (tagName.startsWith("mj-")) {
      return mjmlHandler(node);
    }
    node.getAttributeNames().forEach((name) => {
      let key = camelCase(name);
      if (key === "class") {
        key = "className";
      }
      attrs[key] = node.getAttribute(name) || "";
    });
    if (tagName === "br") {
      return BlockManager.getBlockByType(ElementType.LINE_BREAK).create({
        attributes: {},
        data: {},
        children: [{ text: "" }],
      });
    }

    if (blockLevelTags.includes(tagName)) {
      return BlockManager.getBlockByType(ElementType.HTML_BLOCK_NODE).create({
        attributes: attrs,
        data: {
          tagName: tagName,
        },
        children: [...node.childNodes]
          .filter((item) => item.nodeType !== Node.COMMENT_NODE)
          .map((child) => getDOMNode(child, mjmlHandler)),
      });
    }

    return BlockManager.getBlockByType(ElementType.HTML_NODE).create({
      attributes: attrs,
      data: {
        tagName: tagName,
      },
      children: [...node.childNodes]
        .filter((item) => item.nodeType !== Node.COMMENT_NODE)
        .map((child) => getDOMNode(child, mjmlHandler)),
    });
  }
  throw new Error("Invalid node");
};
